import React, { createContext, ReactElement, useCallback, useMemo, useState } from 'react';
import { VistaAsset } from '@design-stack-vista/vista-assets-sdk';
import { useDebugContext } from 'src/hooks/DebugContextHooks';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { Upload } from 'src/services/UploadAndPrintService';
import QueryStringUtil from 'src/utils/QueryStringUtil';

export interface UploadContextProps {
  upload: Upload | null;
  setUpload: (newUploads: Upload | null) => void;
  isUploading: boolean;
  setIsUploading: (isUploading: boolean) => void;
  isPreUploaded: boolean;
  uploadRestrictionFailed: boolean;
  setUploadRestrictionFailed: React.Dispatch<React.SetStateAction<boolean>>;
  fileSize: number;
  setFileSize: React.Dispatch<React.SetStateAction<number>>;
  currentUploads: { upload: Promise<VistaAsset | void>; abort: () => void }[];
  setCurrentUploads: React.Dispatch<React.SetStateAction<{ upload: Promise<VistaAsset | void>; abort: () => void }[]>>;
  uploadMethod: 'drag_and_drop' | 'click';
  setUploadMethod: React.Dispatch<React.SetStateAction<'drag_and_drop' | 'click'>>;
}

export const UploadContext = createContext<UploadContextProps>({
  upload: null,
  setUpload: () => {
    return;
  },
  isUploading: false,
  setIsUploading: () => {
    return;
  },
  isPreUploaded: false,
  uploadRestrictionFailed: false,
  setUploadRestrictionFailed: () => {
    return;
  },
  fileSize: 0,
  setFileSize: () => {
    return;
  },
  currentUploads: [],
  setCurrentUploads: () => {
    return;
  },
  uploadMethod: 'click',
  setUploadMethod: () => {
    return;
  },
});

export interface UploadContextProviderProps {
  children?: React.ReactNode;
}

export const UploadContextProvider = ({ children }: UploadContextProviderProps): ReactElement => {
  const { debugMode } = useDebugContext();

  const fileUri = QueryStringUtil.getValueByKey('fileUri');
  const isPreUploaded = Boolean(fileUri);

  const [upload, setUploadInternal] = useState<Upload | null>(fileUri ? { fileUri } : null);
  const [uploadLocalStorage, setUploadLocalStorage] = useLocalStorage<Upload | null>('vp-dst-upload', null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadRestrictionFailed, setUploadRestrictionFailed] = useState(false);
  const [fileSize, setFileSize] = useState(0);
  const [currentUploads, setCurrentUploads] = useState<{ upload: Promise<VistaAsset | void>; abort: () => void }[]>([]);
  const [uploadMethod, setUploadMethod] = useState<'drag_and_drop' | 'click'>('click');

  const setUpload = useCallback(
    (upload: Upload | null) => {
      setUploadInternal(upload);
      if (debugMode) {
        setUploadLocalStorage(upload);
      }
    },
    [setUploadInternal, setUploadLocalStorage, debugMode]
  );

  const value = useMemo(() => {
    return {
      upload: !debugMode || isPreUploaded ? upload : uploadLocalStorage,
      setUpload,
      isUploading,
      setIsUploading,
      isPreUploaded,
      uploadRestrictionFailed,
      setUploadRestrictionFailed,
      fileSize,
      setFileSize,
      currentUploads,
      setCurrentUploads,
      uploadMethod,
      setUploadMethod,
    };
  }, [
    currentUploads,
    debugMode,
    fileSize,
    isPreUploaded,
    isUploading,
    setUpload,
    upload,
    uploadLocalStorage,
    uploadRestrictionFailed,
    uploadMethod,
  ]);

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
};
